// ══════════════════════════════════════════════════════════════════════════════
//  src/components/guards/TeacherRoute.jsx
//  LA PUERTA DEL PORTAL DEL PROFESOR
//
//  ── POR QUE NO SE REUSA `ProtectedRoute` ────────────────────────────────────
//  Porque manda a `/onboarding` a todo el que no tenga `learning_profile`, y un
//  profesor creado desde el CRM no tiene ninguno: nunca hizo el diagnostico de
//  nivel. Pasar el portal por ahi lo dejaria dando vueltas en el onboarding de
//  un alumno para siempre, sin poder llegar nunca a su agenda.
//
//  El portal del profesor no es una superficie de aprendizaje. No necesita
//  perfil de aprendizaje.
//
//  ── LA REGLA QUE MANDA AQUI ─────────────────────────────────────────────────
//  **Este guardia no es la seguridad.** El `user_type` de `localStorage` lo
//  puede editar cualquiera desde las herramientas del navegador en dos clics.
//  Quien decide es el servidor: las seis rutas de `/api/teacher` contestan 403
//  a todo el que no sea `teacher`, y eso no se puede tocar desde el navegador.
//
//  Lo que hace este guardia es AHORRAR el viaje y el parpadeo: si en el
//  navegador ya consta que no es profesor, no tiene sentido pintar el portal
//  para que el servidor lo tumbe medio segundo despues. Y aun asi confirma
//  contra el servidor antes de dejar pasar, porque el dato local puede estar
//  viejo — a un profesor se le puede haber dado de baja mientras tenia la
//  pestana abierta.
// ══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

export default function TeacherRoute() {
  // null = todavia preguntando · true = pasa · false = fuera
  const [puedePasar, setPuedePasar] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPuedePasar(false);
      return;
    }

    // El atajo local: si aqui ya consta que no es profesor, ni preguntamos.
    // Es una pista, no una decision: por eso abajo se pregunta igualmente.
    let tipoLocal = null;
    try {
      tipoLocal = JSON.parse(localStorage.getItem('user') || '{}')?.user_type;
    } catch {
      tipoLocal = null;
    }
    if (tipoLocal && tipoLocal !== 'teacher') {
      setPuedePasar(false);
      return;
    }

    let vivo = true;
    (async () => {
      try {
        // ── UN 429 NO ES UN «NO ERES PROFESOR» ──────────────────────────────
        // Es el freno de `/api/teacher/me`: nunca dos peticiones a menos de
        // 250 ms. En desarrollo React ejecuta este efecto dos veces y el portal
        // pide su ficha a la vez; en producción basta un doble clic o dos
        // pestañas. Leído como un «no», mandaba a un profesor a `/entrenar`
        // según a qué petición le tocara el freno (medido el 11-09-2026: una de
        // cada dos cargas de `audita_ganancias`). Se espera lo que pide el
        // servidor y se vuelve a preguntar.
        for (let intento = 0; intento < 4; intento += 1) {
          const r = await fetch('/api/teacher/me', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!vivo) return;
          if (r.status !== 429) {
            setPuedePasar(r.ok);
            return;
          }
          const espera = Number(r.headers.get('Retry-After')) || 1;
          await new Promise((listo) => setTimeout(listo, espera * 1000));
          if (!vivo) return;
        }
        setPuedePasar(false);
      } catch {
        // Un fallo de red no es un «no eres profesor». Pero tampoco podemos
        // afirmar que si lo es, y el portal sin datos no sirve de nada: se
        // deja fuera y que vuelva a entrar. Es la opcion segura de las dos.
        if (vivo) setPuedePasar(false);
      }
    })();

    return () => { vivo = false; };
  }, []);

  if (puedePasar === null) {
    return (
      <div style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        fontFamily: "'DM Sans', sans-serif", color: '#64748b',
      }}>
        Abriendo tu portal…
      </div>
    );
  }

  if (!puedePasar) {
    // A `/entrenar` y no a `/login`: quien llega aqui suele tener sesion
    // valida y simplemente no es profesor. Mandarlo al login le haria creer
    // que se le caduco la sesion, que es una explicacion falsa.
    const hayToken = Boolean(localStorage.getItem('token'));
    return <Navigate to={hayToken ? '/entrenar' : '/login'} replace />;
  }

  return <Outlet />;
}
